import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCookieConsentContext } from '../context/CookieConsentContext';
import { 
  initializeAnalytics,
  identifyUser,
  trackPageView,
  trackFirstVisit,
  trackSessionStart,
  trackScrollEngagement,
  trackClick,
  trackSearch,
  trackOutboundLink,
  trackSignUp,
  trackLogin,
  trackFormSubmit,
  trackCustomEvent,
  trackError,
  setAnalyticsEnabled 
} from '../services/analyticsService';
import { logger } from '../utils/logger';

/**
 * Analytics React Hooks
 * 
 * React hooks that integrate the analytics service with React components
 * and provide automatic tracking for common events
 */

/**
 * Main analytics hook that handles initialization and user identification
 * Should be used in the main App component
 */
export const useAnalytics = () => {
  const { currentUser, userProfile } = useAuth();
  const { isCategoryEnabled } = useCookieConsentContext();
  const location = useLocation();
  const isInitialized = useRef(false);
  const sessionStarted = useRef(false);

  // Initialize analytics when user consents
  useEffect(() => {
    const analyticsConsent = isCategoryEnabled('analytics');
    
    if (analyticsConsent && !isInitialized.current) {
      const success = initializeAnalytics();
      if (success) {
        isInitialized.current = true;
        trackFirstVisit();
        
        // Start session if not already started
        if (!sessionStarted.current) {
          trackSessionStart();
          sessionStarted.current = true;
        }
      }
    } else if (!analyticsConsent && isInitialized.current) {
      // Disable analytics if consent is revoked
      setAnalyticsEnabled(false);
      isInitialized.current = false;
    }
  }, [isCategoryEnabled]);

  // Identify user when authentication state changes
  useEffect(() => {
    if (isInitialized.current) {
      identifyUser(currentUser, userProfile);
    }
  }, [currentUser, userProfile]);

  // Track page views on route changes
  useEffect(() => {
    if (isInitialized.current) {
      trackPageView({
        page_title: document.title,
        page_location: window.location.href,
        page_referrer: document.referrer
      });
    }
  }, [location]);

  return {
    isAnalyticsEnabled: isInitialized.current,
    trackPageView,
    trackCustomEvent,
    trackError
  };
};

/**
 * Hook for tracking scroll engagement
 * Automatically tracks when user scrolls past specified threshold
 */
export const useScrollTracking = (threshold: number = 90) => {
  const { isCategoryEnabled } = useCookieConsentContext();
  const tracked = useRef(false);

  useEffect(() => {
    if (!isCategoryEnabled('analytics') || tracked.current) return;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercentage = (scrollTop / documentHeight) * 100;

      if (scrollPercentage >= threshold && !tracked.current) {
        trackScrollEngagement(scrollPercentage);
        tracked.current = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold, isCategoryEnabled]);

  return { tracked: tracked.current };
};

/**
 * Hook for tracking click events on specific elements
 * Returns a click handler that can be attached to elements
 */
export const useClickTracking = () => {
  const { isCategoryEnabled } = useCookieConsentContext();

  const trackElementClick = useCallback((
    event: React.MouseEvent,
    elementType: string,
    additionalData?: any
  ) => {
    if (!isCategoryEnabled('analytics')) return;

    const target = event.currentTarget as HTMLElement;
    const clickData = {
      element_type: elementType,
      element_id: target.id,
      element_class: target.className,
      element_text: target.textContent?.substring(0, 100),
      ...additionalData
    };

    trackClick(clickData);
  }, [isCategoryEnabled]);

  return { trackElementClick };
};

/**
 * Hook for tracking outbound links
 * Automatically detects and tracks clicks on external links
 */
export const useOutboundLinkTracking = () => {
  const { isCategoryEnabled } = useCookieConsentContext();

  useEffect(() => {
    if (!isCategoryEnabled('analytics')) return;

    const handleLinkClick = (event: Event) => {
      const target = event.target as HTMLAnchorElement;
      
      if (target.tagName === 'A' && target.href) {
        const currentHost = window.location.hostname;
        const linkHost = new URL(target.href).hostname;
        
        if (linkHost !== currentHost) {
          trackOutboundLink(target.href, target.textContent || undefined);
        }
      }
    };

    document.addEventListener('click', handleLinkClick);
    return () => document.removeEventListener('click', handleLinkClick);
  }, [isCategoryEnabled]);
};

/**
 * Hook for tracking search events
 */
export const useSearchTracking = () => {
  const { isCategoryEnabled } = useCookieConsentContext();

  const trackSearchEvent = useCallback((
    searchTerm: string,
    contentType?: string,
    resultsCount?: number
  ) => {
    if (!isCategoryEnabled('analytics')) return;

    trackSearch({
      search_term: searchTerm,
      content_type: contentType,
      results_count: resultsCount
    });
  }, [isCategoryEnabled]);

  return { trackSearchEvent };
};

/**
 * Hook for tracking form submissions
 */
export const useFormTracking = () => {
  const { isCategoryEnabled } = useCookieConsentContext();

  const trackFormSubmission = useCallback((
    formId: string,
    formName?: string,
    method?: string
  ) => {
    if (!isCategoryEnabled('analytics')) return;

    trackFormSubmit({
      form_id: formId,
      form_name: formName,
      method: method
    });
  }, [isCategoryEnabled]);

  return { trackFormSubmission };
};

/**
 * Hook for tracking authentication events
 */
export const useAuthTracking = () => {
  const { isCategoryEnabled } = useCookieConsentContext();

  const trackSignUpEvent = useCallback((method: string = 'email') => {
    if (!isCategoryEnabled('analytics')) return;
    trackSignUp(method);
  }, [isCategoryEnabled]);

  const trackLoginEvent = useCallback((method: string = 'email') => {
    if (!isCategoryEnabled('analytics')) return;
    trackLogin(method);
  }, [isCategoryEnabled]);

  return { trackSignUpEvent, trackLoginEvent };
};

/**
 * Hook for tracking errors with automatic error boundary integration
 */
export const useErrorTracking = () => {
  const { isCategoryEnabled } = useCookieConsentContext();

  const trackErrorEvent = useCallback((
    error: Error,
    context?: string
  ) => {
    if (!isCategoryEnabled('analytics')) return;
    
    trackError(error, context);
    logger.error(`🚨 Error tracked: ${error.message}`, { context });
  }, [isCategoryEnabled]);

  return { trackErrorEvent };
};

/**
 * Hook for tracking custom events
 */
export const useCustomEventTracking = () => {
  const { isCategoryEnabled } = useCookieConsentContext();

  const trackEvent = useCallback((
    eventName: string,
    parameters?: any
  ) => {
    if (!isCategoryEnabled('analytics')) return;
    
    trackCustomEvent(eventName, parameters);
  }, [isCategoryEnabled]);

  return { trackEvent };
};

/**
 * Hook for performance monitoring
 * Tracks page load times and web vitals
 */
export const usePerformanceTracking = () => {
  const { isCategoryEnabled } = useCookieConsentContext();

  useEffect(() => {
    if (!isCategoryEnabled('analytics')) return;

    // Track page load time
    window.addEventListener('load', () => {
      const loadTime = window.performance.now();
      trackCustomEvent('page_load_time', {
        load_time_ms: Math.round(loadTime),
        page_location: window.location.href
      });
    });

    // Track navigation timing if available
    if ('performance' in window && 'getEntriesByType' in window.performance) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const navigation = window.performance?.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
          if (navigation) {
            trackCustomEvent('navigation_timing', {
              dns_time: Math.round(navigation.domainLookupEnd - navigation.domainLookupStart),
              connect_time: Math.round(navigation.connectEnd - navigation.connectStart),
              response_time: Math.round(navigation.responseEnd - navigation.responseStart),
              dom_load_time: Math.round(navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart),
              page_location: window.location.href
            });
          }
        }, 1000);
      });
    }
  }, [isCategoryEnabled]);
};

/**
 * Comprehensive analytics provider hook
 * Combines all analytics functionality for easy setup
 */
export const useComprehensiveAnalytics = () => {
  const analytics = useAnalytics();
  const { tracked: scrollTracked } = useScrollTracking();
  const { trackElementClick } = useClickTracking();
  const { trackSearchEvent } = useSearchTracking();
  const { trackFormSubmission } = useFormTracking();
  const { trackSignUpEvent, trackLoginEvent } = useAuthTracking();
  const { trackErrorEvent } = useErrorTracking();
  const { trackEvent } = useCustomEventTracking();

  // Setup outbound link tracking
  useOutboundLinkTracking();
  
  // Setup performance tracking
  usePerformanceTracking();

  return {
    ...analytics,
    scrollTracked,
    trackElementClick,
    trackSearchEvent,
    trackFormSubmission,
    trackSignUpEvent,
    trackLoginEvent,
    trackErrorEvent,
    trackEvent
  };
};
